import { DateTime } from "luxon";

import {
  CustomerWindowServerMessageSchema,
  isCustomerWindowFresh,
  type CustomerListWindowSnapshot,
  type CustomerWindowAuthorization,
  type CustomerWindowTab,
} from "@/lib/realtime/customer-projection/window-contract";

import {
  CustomerPageResponseSchema,
  type CustomerPageResponse,
} from "@/dashboard/features/hub/lib/customer/row-schema";
import type { HubFilters } from "@/dashboard/features/hub/lib/filters/hub-filters";

type CustomerWindowResult =
  | { kind: "window"; page: CustomerPageResponse; window: CustomerListWindowSnapshot }
  | { kind: "fallback"; resubscribe: boolean }
  | { kind: "ignore" };

/** Validate complete, viewer-scoped windows before they can own a sidebar query. */
export class CustomerListWindowClient {
  private window: CustomerListWindowSnapshot | null = null;
  private latestSourceReadAt = 0;
  private latestLeaseExpiresAt = 0;
  private latestVersion: string | null = null;

  static supportsView(
    filters: HubFilters,
    search: string | undefined,
    tab: string,
    isCallTaker: boolean
  ): boolean {
    return (
      !isCallTaker &&
      (tab === "all" || tab === "transferred") &&
      !search?.trim() &&
      !Object.values(filters).some(value => (Array.isArray(value) ? value.length > 0 : !!value))
    );
  }

  constructor(
    private readonly subscriptionId: string,
    private readonly authorization: CustomerWindowAuthorization,
    private readonly tenantIds: ReadonlySet<string>,
    private readonly tab: CustomerWindowTab,
    private readonly minimumSourceReadAt = 0
  ) {}

  isFresh(now = DateTime.utc().toMillis()): boolean {
    return !!this.window && isCustomerWindowFresh(this.window, now);
  }

  reset(): void {
    this.window = null;
  }

  accept(payload: unknown, now = DateTime.utc().toMillis()): CustomerWindowResult {
    const parsed = CustomerWindowServerMessageSchema.safeParse(payload);
    if (!parsed.success) {
      return this.fallback();
    }
    const message = parsed.data;
    if (message.subscriptionId !== this.subscriptionId) {
      return { kind: "ignore" };
    }
    if (message.type === "customer_window_unavailable") {
      return this.fallback(false);
    }
    if (
      message.authorizationId !== this.authorization.authorizationId ||
      message.scopeKey !== this.authorization.scopeKey ||
      message.policyKey !== this.authorization.policyKey ||
      message.tab !== this.tab ||
      !isCustomerWindowFresh(message.window, now)
    ) {
      return this.fallback();
    }
    const sourceReadAt = DateTime.fromISO(message.window.sourceReadAt).toMillis();
    const leaseExpiresAt = DateTime.fromISO(message.window.leaseExpiresAt).toMillis();
    if (
      sourceReadAt < Math.max(this.minimumSourceReadAt, this.latestSourceReadAt) ||
      (sourceReadAt === this.latestSourceReadAt && leaseExpiresAt > this.latestLeaseExpiresAt) ||
      (message.window.version === this.latestVersion &&
        (sourceReadAt !== this.latestSourceReadAt ||
          leaseExpiresAt !== this.latestLeaseExpiresAt)) ||
      (message.type === "customer_window_snapshot" && !!this.window) ||
      (message.type === "customer_window_delta" &&
        (!this.window ||
          message.previousVersion !== this.window.version ||
          message.window.version === this.window.version ||
          sourceReadAt < this.latestSourceReadAt))
    ) {
      return this.fallback();
    }
    const page = CustomerPageResponseSchema.safeParse({
      data: message.window.rows,
      next_cursor: message.window.nextCursor,
    });
    if (!page.success) {
      return this.fallback();
    }
    const customerIds = new Set<string>();
    for (const row of page.data.data) {
      if (!this.tenantIds.has(row.tenant_id) || customerIds.has(row.id)) {
        return this.fallback();
      }
      customerIds.add(row.id);
    }
    this.window = message.window;
    this.latestSourceReadAt = sourceReadAt;
    this.latestLeaseExpiresAt = leaseExpiresAt;
    this.latestVersion = message.window.version;
    return { kind: "window", page: page.data, window: message.window };
  }

  private fallback(resubscribe = true): CustomerWindowResult {
    this.reset();
    return { kind: "fallback", resubscribe };
  }
}
